import { createContext, useContext, useMemo, type ReactNode } from 'react';
import type { Business } from '@hero/core';
import {
  getReferralAttributionFields,
  getReferralCode,
  getReferralCodeCurrent,
  getReferralVisitorId,
  withReferralCode,
} from './referralStorage';
import { useReferralCapture } from './useReferralCapture';

export interface ReferralContextValue {
  /** Which line this app belongs to (passed to the provider). */
  business: Business;
  /** The original (first-touch) code on file, read at call time. */
  getCode: () => string | undefined;
  /** The latest code seen on this browser, read at call time. */
  getCodeCurrent: () => string | undefined;
  /** The `hero_vid` visitor id (minted on first call). */
  getVisitorId: () => string | undefined;
  /** `{ referralCode?, referralVisitorId? }` for forms and signup payloads. */
  getAttributionFields: typeof getReferralAttributionFields;
  /** Append `?r=CODE` to an outbound URL when a code is stored. */
  withCode: (url: string) => string;
  /**
   * Record a CTA click for the stored code: posts a `click` tracking
   * hit to Hero's ledger. No PostHog event — the CTA's own product
   * event is the record there. No-op without a stored code.
   */
  trackClick: (target: string) => void;
}

const ReferralContext = createContext<ReferralContextValue | null>(null);

export interface ReferralProviderProps {
  /** Which line this surface sells; scopes `closest_tracking` and PostHog props. */
  business: Business;
  /** UI language, stamped on tracking hits. */
  language?: string;
  /** Skip the automatic `view` hit. Default `true` (send it). */
  trackView?: boolean;
  children: ReactNode;
}

/**
 * Mount once per web app, inside `<ApiProvider>` (it posts hits through
 * the SDK). Captures `?r=CODE`, keeps the cookies and PostHog
 * super-properties in sync, and exposes `useReferral()` to any
 * component that needs the code or wants to record a CTA click.
 *
 * ```tsx
 * <ApiProvider api={heroAPI}>
 *   <ReferralProvider business="hero" language={locale}>
 *     <App />
 *   </ReferralProvider>
 * </ApiProvider>
 * ```
 */
export function ReferralProvider({
  business,
  language,
  trackView,
  children,
}: ReferralProviderProps) {
  const { trackClick } = useReferralCapture({ business, language, trackView });

  const value = useMemo<ReferralContextValue>(
    () => ({
      business,
      getCode: getReferralCode,
      getCodeCurrent: getReferralCodeCurrent,
      getVisitorId: getReferralVisitorId,
      getAttributionFields: getReferralAttributionFields,
      withCode: url => withReferralCode(url),
      trackClick,
    }),
    [business, trackClick],
  );

  return <ReferralContext.Provider value={value}>{children}</ReferralContext.Provider>;
}

/**
 * For CTAs rendered with custom markup (Next `<Link>`, a design-system
 * button): returns a click handler that records the referral click and
 * then runs the caller's own handler, plus `href` decorated with `?r=`
 * when `decorateHref` is set. `<ReferralCta>` in `@hero/web-components`
 * wraps this for the plain `<a>` / `<button>` case.
 */
export function useReferralCta<E = unknown>(
  target: string,
  options: { onClick?: (event: E) => void; href?: string; decorateHref?: boolean } = {},
): { onClick: (event: E) => void; href: string | undefined } {
  const { trackClick, withCode } = useReferral();
  const onClick = (event: E) => {
    trackClick(target);
    options.onClick?.(event);
  };
  const href = options.href && options.decorateHref ? withCode(options.href) : options.href;
  return { onClick, href };
}

const noop = () => undefined;

/**
 * Access the referral context. Safe to call outside a provider (SSR,
 * storybook, a shared component mounted in an app that does not run
 * the program): the storage getters still work and `trackClick` is a
 * no-op, so `<ReferralCta>` never throws.
 */
export function useReferral(): ReferralContextValue {
  const ctx = useContext(ReferralContext);
  if (ctx) return ctx;
  return {
    business: 'hero',
    getCode: getReferralCode,
    getCodeCurrent: getReferralCodeCurrent,
    getVisitorId: getReferralVisitorId,
    getAttributionFields: getReferralAttributionFields,
    withCode: url => withReferralCode(url),
    trackClick: noop,
  };
}
